import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { 
  Save, 
  Loader2, 
  AlertCircle, 
  CheckCircle2, 
  Upload,
  Globe,
  Mail,
  Phone,
  MessageCircle,
  MapPin
} from 'lucide-react';

const AdminSettings = () => {
  const [settings, setSettings] = useState<any>({
    site_name: 'Walnut Farm',
    logo_url: '',
    phone: '',
    email: '',
    whatsapp: '918900992020',
    address: 'Noida, Uttar Pradesh, India',
    map_url: '',
    social_links: {
      facebook: '',
      twitter: '',
      instagram: ''
    },
    footer_text: '',
    seo_title: '',
    seo_description: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const isValidUrl = (url: string) => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      if (data) {
        const socialLinks = {
          facebook: '',
          twitter: '',
          instagram: '',
          ...(typeof data.social_links === 'object' ? data.social_links : {})
        };
        setSettings({ ...data, social_links: socialLinks });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isValidUrl(settings.social_links.facebook) || !isValidUrl(settings.social_links.twitter)) {
      setMessage({ type: 'error', text: 'Please enter valid URLs for social links.' });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const { id, created_at, updated_at, ...saveData } = settings;
      let error;
      
      if (id) {
        ({ error } = await supabase.from('site_settings').update(saveData).eq('id', id));
      } else {
        ({ error } = await supabase.from('site_settings').insert([saveData]));
      }

      if (error) throw error;
      setMessage({ type: 'success', text: 'Settings updated successfully!' });
      fetchSettings();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSaving(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;
      const filePath = `branding/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('site-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('site-assets')
        .getPublicUrl(filePath);

      setSettings({ ...settings, logo_url: publicUrl });
      setMessage({ type: 'success', text: 'Logo uploaded. Remember to save changes!' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-emerald" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-8 max-w-4xl mx-auto">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-serif text-emerald">General Settings</h1>
            <p className="text-[#59635C] font-clean mt-2">Manage branding, SEO, and contact details.</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald text-ivory px-8 py-3 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/90 transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Settings
          </button>
        </div>

        {message && (
          <div className={`p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-clean font-medium">{message.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Branding Section */}
          <div className="bg-white p-6 rounded-2xl border border-accent/10 shadow-sm space-y-6">
            <h3 className="text-xl font-serif text-emerald border-b border-accent/5 pb-2">Branding & SEO</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Logo</label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-[#F5F1E8] rounded-xl flex items-center justify-center overflow-hidden border border-accent/10">
                    {settings.logo_url ? (
                      <img src={settings.logo_url} alt="Logo" className="w-full h-full object-contain p-2" />
                    ) : (
                      <Globe className="w-8 h-8 text-accent/20" />
                    )}
                  </div>
                  <input type="file" ref={logoInputRef} onChange={handleLogoUpload} className="hidden" accept="image/*" />
                  <button 
                    onClick={() => logoInputRef.current?.click()}
                    className="text-xs font-bold uppercase tracking-widest text-emerald hover:underline flex items-center gap-2"
                  >
                    <Upload className="w-3 h-3" /> Change Logo
                  </button>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Site Title</label>
                <input
                  type="text"
                  value={settings.site_name}
                  onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Meta Description (SEO)</label>
                <textarea
                  value={settings.seo_description}
                  onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Contact & Socials */}
          <div className="bg-white p-6 rounded-2xl border border-accent/10 shadow-sm space-y-6">
            <h3 className="text-xl font-serif text-emerald border-b border-accent/5 pb-2">Contact & Socials</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1 flex items-center gap-2">
                  <Mail className="w-3 h-3" /> Contact Email
                </label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1 flex items-center gap-2">
                  <Phone className="w-3 h-3" /> Phone Number
                </label>
                <input
                  type="text"
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1 flex items-center gap-2 text-green-600">
                  <MessageCircle className="w-3 h-3" /> WhatsApp Number (91...)
                </label>
                <input
                  type="text"
                  value={settings.whatsapp}
                  onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1 flex items-center gap-2">
                  <MapPin className="w-3 h-3" /> Property Address
                </label>
                <input
                  type="text"
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30"
                />
              </div>

              <div className="pt-2 space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1 flex items-center gap-2">
                    Facebook URL
                  </label>
                  <input
                    type="text"
                    placeholder="https://facebook.com/..."
                    value={settings.social_links.facebook}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      social_links: { ...settings.social_links, facebook: e.target.value } 
                    })}
                    className={`w-full px-4 py-3 rounded-lg border outline-none font-clean bg-[#F5F1E8]/30 text-xs ${!isValidUrl(settings.social_links.facebook) ? 'border-red-500' : 'border-accent/20 focus:border-emerald'}`}
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1 flex items-center gap-2">
                    Twitter/X URL
                  </label>
                  <input
                    type="text"
                    placeholder="https://twitter.com/..."
                    value={settings.social_links.twitter}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      social_links: { ...settings.social_links, twitter: e.target.value } 
                    })}
                    className={`w-full px-4 py-3 rounded-lg border outline-none font-clean bg-[#F5F1E8]/30 text-xs ${!isValidUrl(settings.social_links.twitter) ? 'border-red-500' : 'border-accent/20 focus:border-emerald'}`}
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1 flex items-center gap-2">
                    Instagram URL
                  </label>
                  <input
                    type="text"
                    placeholder="https://instagram.com/..."
                    value={settings.social_links.instagram}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      social_links: { ...settings.social_links, instagram: e.target.value } 
                    })}
                    className={`w-full px-4 py-3 rounded-lg border outline-none font-clean bg-[#F5F1E8]/30 text-xs ${!isValidUrl(settings.social_links.instagram) ? 'border-red-500' : 'border-accent/20 focus:border-emerald'}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
